// Audio plumbing for voice mode (#2579): microphone samples in, 24 kHz PCM16 frames out, and the
// spoken answer scheduled back per turn so a cancel can drop exactly one turn's audio.

/** The format the voice hub speaks in both directions: PCM16, 24 kHz, mono. */
export const VOICE_SAMPLE_RATE = 24000;

/** 40 ms per frame: inside the server's 10–100 ms window, and few enough messages per second. */
export const VOICE_FRAME_SAMPLES = 960;

const LOW_PASS_TAPS = 7;

/** Hamming-windowed sinc at the output's Nyquist, normalised to unity gain at DC. */
const lowPassTaps = (ratio: number): Float32Array => {
    const cutoff = 0.5 / ratio;
    const middle = (LOW_PASS_TAPS - 1) / 2;
    const taps = Float32Array.from({ length: LOW_PASS_TAPS }, (_, n) => {
        const x = n - middle;
        const sinc = x === 0 ? 2 * cutoff : Math.sin(2 * Math.PI * cutoff * x) / (Math.PI * x);
        return sinc * (0.54 - 0.46 * Math.cos((2 * Math.PI * n) / (LOW_PASS_TAPS - 1)));
    });
    const sum = taps.reduce((a, b) => a + b, 0);
    return taps.map((tap) => tap / sum);
};

/**
 * Linear-interpolation resampler that keeps its fractional position across chunks, so a stream of
 * 128-sample worklet chunks or 2048-sample processor chunks resamples without clicks at the seams.
 * Downsampling first runs a short low-pass: at 48 → 24 kHz interpolation alone is plain decimation,
 * which folds everything above 12 kHz (sibilants, hiss) back into the speech band the transcriber hears.
 */
export class Resampler {
    private readonly ratio: number;
    private readonly taps: Float32Array | undefined;
    // The last taps − 1 input samples, so the filter runs across chunk seams.
    private history: Float32Array;
    // Position of the next output sample in a window whose index 0 is the previous chunk's last
    // sample and 1..n is the current chunk — so interpolation can bridge the seam.
    private position = 1;
    private previous = 0;

    constructor(inputRate: number, outputRate = VOICE_SAMPLE_RATE) {
        this.ratio = inputRate / outputRate;
        this.taps = this.ratio > 1 ? lowPassTaps(this.ratio) : undefined;
        this.history = new Float32Array(this.taps ? this.taps.length - 1 : 0);
    }

    process(chunk: Float32Array): Float32Array {
        if (this.ratio === 1) {
            return chunk.slice();
        }

        const input = this.lowPass(chunk);
        const n = input.length;
        const at = (i: number) => (i === 0 ? this.previous : input[i - 1]);
        const output: number[] = [];
        while (this.position < n) {
            const i = Math.floor(this.position);
            const fraction = this.position - i;
            output.push(at(i) + (at(i + 1) - at(i)) * fraction);
            this.position += this.ratio;
        }

        this.position -= n;
        if (n > 0) {
            this.previous = input[n - 1];
        }
        return Float32Array.from(output);
    }

    private lowPass(chunk: Float32Array): Float32Array {
        const taps = this.taps;
        if (!taps) {
            return chunk;
        }
        const padded = new Float32Array(this.history.length + chunk.length);
        padded.set(this.history);
        padded.set(chunk, this.history.length);
        const output = new Float32Array(chunk.length);
        for (let i = 0; i < chunk.length; i++) {
            let sum = 0;
            for (let k = 0; k < taps.length; k++) {
                sum += taps[k] * padded[i + k];
            }
            output[i] = sum;
        }
        this.history = padded.slice(chunk.length);
        return output;
    }
}

export const floatToPcm16 = (input: Float32Array): Int16Array => {
    const output = new Int16Array(input.length);
    for (let i = 0; i < input.length; i++) {
        const sample = Math.max(-1, Math.min(1, input[i]));
        output[i] = sample < 0 ? sample * 0x8000 : sample * 0x7fff;
    }
    return output;
};

export const pcm16ToFloat = (input: Int16Array): Float32Array => {
    const output = new Float32Array(input.length);
    for (let i = 0; i < input.length; i++) {
        output[i] = input[i] / 0x8000;
    }
    return output;
};

/** Collects samples and hands out whole frames of exactly `frameSamples`; the rest waits for more. */
export class FrameBatcher {
    private buffer: Int16Array;
    private filled = 0;

    constructor(private readonly frameSamples = VOICE_FRAME_SAMPLES) {
        this.buffer = new Int16Array(frameSamples);
    }

    push(samples: Int16Array): Int16Array[] {
        const frames: Int16Array[] = [];
        let offset = 0;
        while (offset < samples.length) {
            const take = Math.min(this.frameSamples - this.filled, samples.length - offset);
            this.buffer.set(samples.subarray(offset, offset + take), this.filled);
            this.filled += take;
            offset += take;
            if (this.filled === this.frameSamples) {
                frames.push(this.buffer);
                this.buffer = new Int16Array(this.frameSamples);
                this.filled = 0;
            }
        }
        return frames;
    }
}

export const pcm16ToBase64 = (samples: Int16Array): string => {
    const bytes = new Uint8Array(samples.buffer, samples.byteOffset, samples.byteLength);
    let binary = '';
    const chunk = 0x8000;
    for (let i = 0; i < bytes.length; i += chunk) {
        binary += String.fromCharCode(...bytes.subarray(i, i + chunk));
    }
    return btoa(binary);
};

export const base64ToPcm16 = (base64: string): Int16Array => {
    const binary = atob(base64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
        bytes[i] = binary.charCodeAt(i);
    }
    // Copy into an aligned buffer: an odd-length payload would otherwise throw.
    const samples = new Int16Array(Math.floor(bytes.length / 2));
    new Uint8Array(samples.buffer).set(bytes.subarray(0, samples.length * 2));
    return samples;
};

/** A WAV file of PCM16 mono samples at {@link VOICE_SAMPLE_RATE}: the one format the dictation endpoint takes. */
export const pcm16ToWav = (samples: Int16Array): Blob => {
    const header = new DataView(new ArrayBuffer(44));
    const ascii = (offset: number, text: string) => {
        for (let i = 0; i < text.length; i++) {
            header.setUint8(offset + i, text.charCodeAt(i));
        }
    };
    ascii(0, 'RIFF');
    header.setUint32(4, 36 + samples.byteLength, true);
    ascii(8, 'WAVE');
    ascii(12, 'fmt ');
    header.setUint32(16, 16, true);
    header.setUint16(20, 1, true);
    header.setUint16(22, 1, true);
    header.setUint32(24, VOICE_SAMPLE_RATE, true);
    header.setUint32(28, VOICE_SAMPLE_RATE * 2, true);
    header.setUint16(32, 2, true);
    header.setUint16(34, 16, true);
    ascii(36, 'data');
    header.setUint32(40, samples.byteLength, true);
    // PCM16 in WAV is little-endian, as typed arrays are on every platform a browser runs on.
    const bytes = new Uint8Array(samples.buffer, samples.byteOffset, samples.byteLength);
    return new Blob([header.buffer, bytes.slice()], { type: 'audio/wav' });
};

/** The slice of Web Audio the playback queue needs, so it can be tested without a browser. */
export interface PlaybackOutput {
    readonly currentTime: number;
    play(samples: Float32Array, startAt: number): { stop(): void; readonly duration: number };
}

/**
 * Plays answer audio back to back and remembers which turn each chunk belongs to: the server
 * cancels one turn at a time (`response.cancelled` with its `turnId`), and barge-in flushes all.
 */
export class PlaybackQueue {
    // When the last chunk queued ends, or was cut off. Minus infinity before the first chunk, and a flush or cancel
    // with nothing playing leaves it in the past: a microphone muted around playback (useVoiceSession) must not
    // silence the start of a question asked while nothing plays.
    private nextStart = Number.NEGATIVE_INFINITY;
    private readonly playing = new Map<string, Array<{ stop(): void; endsAt: number }>>();

    constructor(private readonly output: PlaybackOutput) {}

    enqueue(turnId: string, samples: Float32Array): void {
        this.prune();
        const startAt = Math.max(this.nextStart, this.output.currentTime);
        const handle = this.output.play(samples, startAt);
        this.nextStart = startAt + handle.duration;
        const list = this.playing.get(turnId) ?? [];
        list.push({
            stop: () => {
                handle.stop();
            },
            endsAt: this.nextStart,
        });
        this.playing.set(turnId, list);
    }

    /** Stops and drops one turn's audio, played or still queued; the others keep their place. */
    cancel(turnId: string): void {
        const list = this.playing.get(turnId);
        if (!list) {
            return;
        }
        list.forEach((h) => {
            h.stop();
        });
        this.playing.delete(turnId);
        this.reflow();
    }

    /** Barge-in: the user is speaking, so nothing queued should keep talking over them. */
    flush(): void {
        this.playing.forEach((list) => {
            list.forEach((h) => {
                h.stop();
            });
        });
        this.playing.clear();
        this.nextStart = Math.min(this.nextStart, this.output.currentTime);
    }

    get isPlaying(): boolean {
        this.prune();
        return this.playing.size > 0;
    }

    /** When, on the output's clock, the last audio stops or stopped playing: its end, or when it was cut off. */
    get endsAt(): number {
        return this.nextStart;
    }

    turns(): string[] {
        this.prune();
        return [...this.playing.keys()];
    }

    // A handle holds its source node and buffer (about 96 kB per second of answer), so keeping every
    // chunk that has played would grow for the whole session. A played chunk has nothing left to stop.
    private prune(): void {
        const now = this.output.currentTime;
        this.playing.forEach((list, turnId) => {
            const left = list.filter((h) => h.endsAt > now);
            if (left.length === 0) {
                this.playing.delete(turnId);
            } else {
                this.playing.set(turnId, left);
            }
        });
    }

    private reflow(): void {
        let latest = Math.min(this.nextStart, this.output.currentTime);
        this.playing.forEach((list) => {
            list.forEach((h) => {
                latest = Math.max(latest, h.endsAt);
            });
        });
        this.nextStart = latest;
    }
}

/**
 * A soft rising chime, 130 ms, that tells the user the assistant hears them now: the session takes seconds to
 * come up and nothing said before that is sent. Generated rather than loaded, so there is no asset to fetch.
 * Returns when it ends, on the context's clock.
 */
export const playStartTone = (context: AudioContext): number => {
    const start = context.currentTime;
    const gain = context.createGain();
    gain.gain.setValueAtTime(0, start);
    gain.gain.linearRampToValueAtTime(0.08, start + 0.015);
    gain.gain.exponentialRampToValueAtTime(0.0001, start + 0.12);
    gain.connect(context.destination);
    const tone = context.createOscillator();
    tone.type = 'sine';
    tone.frequency.setValueAtTime(660, start);
    tone.frequency.linearRampToValueAtTime(880, start + 0.08);
    tone.connect(gain);
    tone.onended = () => {
        tone.disconnect();
        gain.disconnect();
    };
    tone.start(start);
    tone.stop(start + 0.13);
    return start + 0.13;
};

/** Root-mean-square level of a chunk, 0..1, for the "listening" indicator. */
export const levelOf = (samples: Float32Array): number => {
    if (samples.length === 0) {
        return 0;
    }
    let sum = 0;
    for (const s of samples) {
        sum += s * s;
    }
    return Math.min(1, Math.sqrt(sum / samples.length) * 4);
};
